import type { FC } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import classNames from "classnames";
import _ from "lodash";

import { colorsPalettes, getBrightnessHEX } from "@similarweb/styles";

import { chosenSites } from "app/common/services/chosenSites";
import type { IChosenItemParams } from "app/components/compare/WebsiteQueryBar/WebsitePageQueryBarUtils";
import type { ITableOptions } from "app/components/React/Table/interfaces/ITableOptions";
import { CHART_COLORS } from "app/constants/ChartColors";
import { i18nFilter } from "app/filters/i18nFilter";
import { percentageFilter } from "app/filters/ngFilters";
import { TrafficShareTooltip } from "pro-features/components/tooltips/src/TrafficShareTooltip/TrafficShareTooltip";

import type { ITableCellProps } from "../interfaces/ITableCellProps";

export interface IRegionalGroupTrafficShareProps extends Omit<ITableCellProps, "tableOptions"> {
    value: any;
    tooltipKey?: string;
    tableOptions?: Partial<ITableOptions> & {
        colorSource?: string;
    };
}
export const RegionalGroupTrafficShare: FC<IRegionalGroupTrafficShareProps> = (props) => {
    const { value = {}, tableOptions, tooltipKey } = props;
    const { params } = useCurrentStateAndParams();
    // @ts-expect-error FIXME: Type 'string' is not assignable to type keyof CHART_COLORS
    const colorKey: keyof typeof CHART_COLORS = tableOptions?.colorSource || "compareMainColors";
    const origColors = CHART_COLORS[colorKey];
    let siteOrigins: FixmeType[] = [];
    const siteOriginsForTooltip: FixmeType[] = [];
    // copy the colors since we might remove some items from it
    // @ts-expect-error FIXME: Property 'slice' does not exist on type 'string | string[] | object
    const colors = origColors.slice();
    const removeColorsAt: number[] = [];

    // turn the object into an array of chosen sites
    if (!_.isArray(value)) {
        if (params.pageFilter) {
            const pageFilters: IChosenItemParams = JSON.parse(params.pageFilter);
            pageFilters.forEach((site, index) => {
                if (value && value[site.url]) {
                    siteOrigins.push({
                        name: site.url,
                        value: value[site.url],
                        // @ts-ignore FIXME: Expression of type 'number' can't be used to index type 'string | string[] | object
                        color: origColors[index],
                    });
                }
            });
        } else {
            chosenSites.forEach((site: string, siteInfo: FixmeType, index: number) => {
                if (value && value[site]) {
                    siteOrigins.push({
                        name: site,
                        value: value[site]?.aggregatedShare,
                        // @ts-ignore FIXME: Expression of type 'number' can't be used to index type 'string | string[] | object
                        color: origColors[index],
                    });
                }
            });
        }
    } else {
        siteOrigins = value;
    }
    siteOrigins.forEach((site) => {
        siteOriginsForTooltip.push({
            name:
                value[site.name]?.domains.length > 1
                    ? `${site.name} (+${value[site.name].domains.length - 1})`
                    : site.name,
            text: `${percentageFilter()(site.value, 1)}%`,
            color: colorsPalettes.carbon[0],
            backgroundColor: site.color,
            width: 0.23,
        });
    });

    let items = _.filter(siteOrigins, (item, index) => {
        const result = item.value ? item.value > 0.0005 : item > 0.0005;
        // remove corresponding color if needed
        if (result === false) {
            removeColorsAt.push(index);
        }
        return result;
    });

    if (removeColorsAt.length > 0) {
        _.pullAt(colors, removeColorsAt);
    }
    //
    let _newTotal = 1;
    const _threshold = 0.02;
    if (items.length > 1) {
        const _firstItem = items[0].value ? items[0].value : items[0],
            _lastItem = items[items.length - 1].value ? items[items.length - 1].value : items[items.length - 1];

        if (_firstItem < _threshold) {
            _newTotal -= _threshold;
        }
        if (_lastItem < _threshold) {
            _newTotal -= _threshold;
        }
    }

    items = items.map((item, index) => {
        let value = item.value ? item.value : item;
        const siteName = value > 0.1 ? item.name : item.name + " " + `${percentageFilter()(value, 1)}%`;
        const displayVal = value > 0.1 ? `${percentageFilter()(value, 1)}%` : "";
        if (value > _threshold) {
            value = _newTotal * value;
        }
        if (value < _threshold && (index === 0 || index === items.length - 1)) {
            value = _threshold;
        }
        const cssWidth = `${value * 100}%`;

        const backgroundColor =
            item.color || chosenSites.getSiteColor(item.name) || colors[item.index] || colors[index];
        const classnames = classNames(`bar-innerText`, `bar-${getBrightnessHEX(backgroundColor)}-background`);
        return (
            <div
                key={index}
                className="bar"
                style={{ width: cssWidth }}
            >
                <div
                    className={classnames}
                    title={siteName}
                    style={{ background: backgroundColor }}
                >
                    {displayVal}
                </div>
            </div>
        );
    });

    return siteOriginsForTooltip.length > 0 ? (
        <TrafficShareTooltip
            trafficShareProps={siteOriginsForTooltip}
            title={i18nFilter()(tooltipKey ?? "analysis.source.search.all.table.columns.shareCompare.tooltip.title")}
        >
            <div className="sw-progress sw-progress-compare">{items}</div>
        </TrafficShareTooltip>
    ) : (
        <div className="sw-progress sw-progress-compare">{items}</div>
    );
};
RegionalGroupTrafficShare.displayName = "RegionalGroupTrafficShare.tsx";
